import type { BatchResult, Leg, ReportConfig, SingleResult } from "types/shim";
import { premiumFields, presetsConfig } from "./const";
import mitt from "mitt";

import { BrowserQRCodeSvgWriter } from "@zxing/browser";

import CryptoJS from "crypto-js";
import { generateID } from "server/utils/tool";
import { langs } from "@/locales/antd";
import { saveAs } from "file-saver";

const SECRET_KEY = import.meta.env.VITE_APP_ENCRYPT_KEY; // 环境变量，32 字节

export const isDev = import.meta.env.VITE_DEV_LOCAL === "true";

/**
 * 加密Token
 * @param token
 * @returns
 */
export function encryptToken(token: string) {
  return CryptoJS.AES.encrypt(token, SECRET_KEY).toString();
}

/**
 * 解密Token
 * @param encrypted
 * @returns
 */
export function decryptToken(encrypted: string) {
  const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
  return bytes.toString(CryptoJS.enc.Utf8);
}

export const emitter = mitt();

/**
 * 将SVG字符串转换为图片DataURL
 * @param svgString SVG字符串
 * @param width 图片宽度
 * @param height 图片高度
 * @returns Promise<string> 图片DataURL
 */
export async function svgStringToImageDataUrl(
  svgString: string,
  width: number,
  height: number
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("无法创建canvas上下文"));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);
      const dataUrl = canvas.toDataURL("image/png");
      URL.revokeObjectURL(url);
      resolve(dataUrl);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("无法加载SVG图像"));
    };

    img.src = url;
  });
}

export async function generateInviteQR(userId: string) {
  const inviteUrl = `${window.location.origin}/register?ref=${userId}`;
  const writer = new BrowserQRCodeSvgWriter();
  const svgElement = writer.write(inviteUrl, 100, 100);
  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(svgElement);
  return await svgStringToImageDataUrl(svgString, 100, 100);
}

export function formatError(data: any) {
  if (typeof data === "string") return data;
  return data.message || data.msg || data.error || data;
}

function base64UrlEncode(array: Uint8Array) {
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export function generateCodeVerifier() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * 生成 Code Verifier 和 Code Challenge
 * @returns
 */
export async function generateCodeChallenge() {
  const verifier = generateCodeVerifier();
  const buffer = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return { verifier, challenge: base64UrlEncode(new Uint8Array(buffer)) };
}

/**
 * 生成随机ID
 * @returns
 */
export function generateRandomId() {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

export function convertBatchToSingle(batchResults: BatchResult[] = []): SingleResult {
  if (!batchResults?.length) {
    return {
      id: generateID(),
      legs: [],
      totalDistance: 0,
      productionEmission: 0,
      transportEmission: 0,
      totalEmission: 0,
      taxInEUR: "0",
      taxInEURBase: "0",
      taxConverted: "0",
      currency: "EUR",
      hasFallback: false,
      calcIds: "",
    };
  }
  const legs: Leg[] = [];
  const validResults = batchResults.filter(r => r.status === "success");
  const currency = validResults[0].currency;
  const calcIds: string[] = [];

  // 优化：只遍历一次数组，同时计算所有需要的汇总值
  const totals = validResults.reduce(
    (acc, item) => {
      acc.totalDistance += Number(item.totalDistance) || 0;
      acc.productionEmission += Number(item.productionEmission) || 0;
      acc.transportEmission += Number(item.transportEmission) || 0;
      acc.totalEmission += Number(item.totalEmission) || 0;
      acc.taxInEUR += Number(item.taxInEUR) || 0;
      acc.taxInEURBase += Number(item.taxInEURBase) || 0;
      acc.taxConverted += Number(item.taxConverted) || 0;
      calcIds.push(item.id);
      return acc;
    },
    {
      totalDistance: 0,
      productionEmission: 0,
      transportEmission: 0,
      totalEmission: 0,
      taxInEUR: 0,
      taxInEURBase: 0,
      taxConverted: 0,
    }
  );
  for (const result of validResults) {
    legs.push(...result.legs);
  }
  const totalEmission = totals.productionEmission + totals.transportEmission;
  return {
    id: generateID(),
    legs,
    totalDistance: Number(totals.totalDistance.toFixed(3)),
    productionEmission: Number(totals.productionEmission.toFixed(3)),
    transportEmission: Number(totals.transportEmission.toFixed(6)),
    totalEmission: Number(totalEmission.toFixed(3)),
    taxInEUR: totals.taxInEUR.toFixed(2),
    taxInEURBase: totals.taxInEURBase.toFixed(2),
    taxConverted: totals.taxConverted.toFixed(2),
    currency,
    hasFallback: validResults.some(r => r.hasFallback),
    calcIds: calcIds.join(","),
  };
}

const langMap = {
  en: "en",
  "zh-cn": "zh-CN",
  "zh-tw": "zh-TW",
  fr: "fr",
  ja: "ja",
  es: "es",
};
// ISO 8601 格式正则 (宽松匹配)
const iso8601Pattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(.\d+)?(Z|[+-]\d{2}:\d{2})?)?$/i;
// 博客日期格式 YYYY-MM-DD
const blogDatePattern = /^\d{4}-\d{2}-\d{2}$/;

export function formatTimeZone(time: string, timezone?: string): string {
  // 首先尝试直接创建Date对象，如果失败再使用严格的格式检查
  let date: Date;

  try {
    // 尝试直接解析日期字符串
    date = new Date(time);

    // 检查是否为有效日期
    if (isNaN(date.getTime())) {
      // 如果直接解析失败，再使用原来的格式检查
      if (!iso8601Pattern.test(time) && !blogDatePattern.test(time)) {
        return "Invalid date";
      }

      // 对于博客日期格式，需要特殊处理
      if (blogDatePattern.test(time)) {
        const [year, month, day] = time.split("-").map(Number);
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(time);
      }
    }
  } catch (e) {
    // 如果解析失败，使用原来的严格检查方法
    if (!iso8601Pattern.test(time) && !blogDatePattern.test(time)) {
      return "Invalid date";
    }

    // 对于博客日期格式，需要特殊处理
    if (blogDatePattern.test(time)) {
      const [year, month, day] = time.split("-").map(Number);
      date = new Date(year, month - 1, day);
    } else {
      date = new Date(time);
    }
  }

  let lang = systemStorage.value.lang || navigator.language.toLowerCase();
  if (lang === "zh" || lang === "zh-cn") {
    lang = "zh-cn";
  } else if (lang === "zh-tw") {
    lang = "zh-tw";
  } else {
    // 创建语言映射表，将系统语言代码映射为日期格式化所需的代码
    const langMap: Record<string, string> = {};
    langs.forEach(locale => {
      const localeCode = locale.locale;
      if (localeCode === "zh-cn") {
        langMap[localeCode] = "zh-CN";
      } else if (localeCode === "zh-tw") {
        langMap[localeCode] = "zh-TW";
      } else if (localeCode.startsWith("zh")) {
        langMap[localeCode] = "zh-CN"; // 默认简体中文
      } else {
        // 其他语言使用系统语言代码并转换为标准格式
        langMap[localeCode] = localeCode.replace(/_/g, "-");
      }
    });

    lang = langMap[lang] || "en";
  }

  try {
    // 使用用户设置的时区或者传入的时区参数
    const userTimezone =
      timezone || systemStorage.value.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

    // 判断是否为博客日期格式 (只有年月日，没有时间部分)
    const isBlogDate = blogDatePattern.test(time);

    const formatterOptions: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: userTimezone, // 明确指定时区
      ...(isBlogDate
        ? {}
        : {
            // 只有非博客日期才包含时分秒
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: lang === "en", // 英文使用 12 小时制
          }),
    };

    const formatter = new Intl.DateTimeFormat(lang, formatterOptions);

    if (isNaN(date.getTime())) {
      return "Invalid date";
    }

    const parts = formatter.formatToParts(date);
    const partMap = parts.reduce(
      (acc, part) => {
        acc[part.type] = part.value;
        return acc;
      },
      {} as Record<string, string>
    );

    // 根据语言和用户设置的日期格式调整输出
    let formattedDate = "";
    if (isBlogDate) {
      // 博客日期只显示年月日
      if (lang === "en") {
        formattedDate = `${partMap.month}/${partMap.day}/${partMap.year}`;
      } else {
        formattedDate = `${partMap.year}-${partMap.month}-${partMap.day}`;
      }
    } else {
      // 完整日期时间格式
      if (lang === "en") {
        formattedDate = `${partMap.month}/${partMap.day}/${partMap.year} ${partMap.hour}:${partMap.minute}:${partMap.second} ${partMap.dayPeriod || ""}`;
      } else {
        formattedDate = `${partMap.year}-${partMap.month}-${partMap.day} ${partMap.hour}:${partMap.minute}:${partMap.second}`;
      }
    }

    // 如果有自定义日期格式，应用它
    const customDateFormat = localStorage.getItem("dateFormat");
    if (customDateFormat && !isBlogDate) {
      // 只对非博客日期应用自定义格式
      switch (customDateFormat) {
        case "DD/MM/YYYY":
          formattedDate = `${partMap.day}/${partMap.month}/${partMap.year} ${partMap.hour}:${partMap.minute}:${partMap.second}`;
          break;
        case "MM/DD/YYYY":
          formattedDate = `${partMap.month}/${partMap.day}/${partMap.year} ${partMap.hour}:${partMap.minute}:${partMap.second}`;
          break;
        case "DD-MM-YYYY":
          formattedDate = `${partMap.day}-${partMap.month}-${partMap.year} ${partMap.hour}:${partMap.minute}:${partMap.second}`;
          break;
        default:
          // 默认格式保持不变
          break;
      }
    }

    // 如果是博客日期且有自定义日期格式，应用它（但不包含时分秒）
    if (customDateFormat && isBlogDate) {
      switch (customDateFormat) {
        case "DD/MM/YYYY":
          formattedDate = `${partMap.day}/${partMap.month}/${partMap.year}`;
          break;
        case "MM/DD/YYYY":
          formattedDate = `${partMap.month}/${partMap.day}/${partMap.year}`;
          break;
        case "DD-MM-YYYY":
          formattedDate = `${partMap.day}-${partMap.month}-${partMap.year}`;
          break;
        default:
          // 默认格式保持不变
          break;
      }
    }

    return formattedDate;
  } catch (e) {
    return "Invalid date";
  }
}

// 数字格式化函数
export function formatNumber(num, config) {
  // 如果输入是字符串，尝试转换为数字
  if (typeof num === "string") {
    const cleanedNum = num.replace(/,/g, "");
    num = parseFloat(cleanedNum);
  }

  // 检查转换后的数字是否有效
  if (typeof num !== "number" || isNaN(num)) return "-";

  // 根据配置决定是否使用千分位
  const numberFormat = localStorage.getItem("numberFormat") || "1,000.00";

  if (numberFormat === "1,000.00") {
    return num.toLocaleString("en-US", { maximumFractionDigits: 6, minimumFractionDigits: 0 });
  } else if (numberFormat === "1.000,00") {
    // 欧洲格式：使用点作为千分位，逗号作为小数点
    const integerPart = Math.floor(num)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    const decimalPart = (num % 1).toFixed(2).substring(1);
    return `${integerPart},${decimalPart}`;
  } else if (numberFormat === "1 000,00") {
    // 空格格式：使用空格作为千分位，逗号作为小数点
    const integerPart = Math.floor(num)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    const decimalPart = (num % 1).toFixed(2).substring(1);
    return `${integerPart},${decimalPart}`;
  }

  return num.toString();
}

// 预加载本地 assets 文件
const modules = import.meta.glob("../assets/images/*.{png,jpg,jpeg}", {
  query: "?url",
  import: "default",
});

/**
 * 从 URL 或本地路径加载图像
 * @param {string} url 图像 URL 或本地路径
 * @returns {Promise<{ data: string; format: string } | null>} 图像 base64 数据和格式
 */
export async function getImageFromUrl(
  url: string = ""
): Promise<{ data: string; format: string } | null> {
  try {
    // 处理 R2 图片地址
    if (url.includes("/r2/")) {
      // 提取文件路径部分，例如从 http://localhost:8787/r2/logos/S0_my1jx-RVks1RLdBjUMuLZQLOkXDWI.jpg 提取 logos/S0_my1jx-RVks1RLdBjUMuLZQLOkXDWI.jpg
      const filePath = url.split("/r2/")[1];
      // 调用专门的 R2 API 端点
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`获取图像失败: ${response.statusText}`);
      }
      const buffer = await response.arrayBuffer();
      // 修复：从URL推断文件类型，而不是依赖可能不正确的Content-Type头
      const fileExtension = filePath.split(".").pop()?.toLowerCase() || "png";
      const contentType = response.headers.get("content-type");
      const format =
        contentType && !contentType.includes("octet-stream")
          ? contentType.split("/")[1].toLowerCase() === "jpeg"
            ? "jpg"
            : contentType.split("/")[1].toLowerCase()
          : fileExtension;

      if (!["png", "jpg", "jpeg"].includes(format)) {
        throw new Error(`不支持的图像格式: ${format}`);
      }
      const mimeType = `image/${format === "jpg" ? "jpeg" : format}`;
      const uint8Array = new Uint8Array(buffer);
      const binary = Array.from(uint8Array)
        .map(byte => String.fromCharCode(byte))
        .join("");
      const base64Data = btoa(binary);
      return { data: `data:${mimeType};base64,${base64Data}`, format: mimeType.toUpperCase() };
    }

    if (url.includes("assets")) {
      const normalizedUrl = `../assets/images/${url.replace("assets/", "")}`;
      const importFn = modules[normalizedUrl];
      if (!importFn) {
        throw new Error(`本地图像不存在: ${url}`);
      }
      const imageUrl = await importFn();
      const response = await fetch(imageUrl as string);
      if (!response.ok) {
        throw new Error(`获取图像失败: ${response.statusText}`);
      }
      const buffer = await response.arrayBuffer();
      const contentType = response.headers.get("content-type") || "image/png";
      const format =
        contentType.split("/")[1].toLowerCase() === "jpeg"
          ? "jpg"
          : contentType.split("/")[1].toLowerCase();
      if (!["png", "jpg", "jpeg"].includes(format)) {
        throw new Error(`不支持的图像格式: ${format}`);
      }
      const mimeType = `image/${format === "jpg" ? "jpeg" : format}`;
      const uint8Array = new Uint8Array(buffer);
      const binary = Array.from(uint8Array)
        .map(byte => String.fromCharCode(byte))
        .join("");
      const base64Data = btoa(binary);
      return { data: `data:${mimeType};base64,${base64Data}`, format: mimeType.toUpperCase() };
    }

    // 远程 URL
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`获取图像失败: ${response.statusText}`);
    }
    const buffer = await response.arrayBuffer();
    const contentType = response.headers.get("content-type") || "image/png";
    const format =
      contentType.split("/")[1].toLowerCase() === "jpeg"
        ? "jpg"
        : contentType.split("/")[1].toLowerCase();
    if (!["png", "jpg", "jpeg"].includes(format)) {
      throw new Error(`不支持的图像格式: ${format}`);
    }
    const mimeType = `image/${format === "jpg" ? "jpeg" : format}`;
    const uint8Array = new Uint8Array(buffer);
    const binary = Array.from(uint8Array)
      .map(byte => String.fromCharCode(byte))
      .join("");
    const base64Data = btoa(binary);
    return { data: `data:${mimeType};base64,${base64Data}`, format: mimeType.toUpperCase() };
  } catch (error) {
    console.error("从URL加载图像失败:", error);
    return null;
  }
}

/**
 * 字符串转换为 ArrayBuffer
 * @param s 字符串
 * @returns
 */
export function s2ab(s: string): ArrayBuffer {
  const buf = new ArrayBuffer(s.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
  return buf;
}

/**
 * 将 Buffer 转换为十六进制字符串
 * @param buffer
 * @returns
 */
export async function bufferToHex(buffer: ArrayBuffer) {
  const hex = Array.from(new Uint8Array(buffer))
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
  return hex;
}

/**
 * 生成 SHA-256 哈希（兼容浏览器和 Workers）
 * @param data
 * @returns
 */
export async function generateHash(data: string) {
  const msgBuffer = new TextEncoder().encode(data);
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
  return bufferToHex(hashBuffer);
}

/**
 * 重置高级字段
 * @param config
 */
export function resetPremiumFields(config: ReportConfig) {
  const defaultConfig = presetsConfig.default;
  premiumFields.forEach(path => {
    const keys = path.split(".");
    let currentConfig = config;
    let currentDefault = defaultConfig;
    for (let i = 0; i < keys.length - 1; i++) {
      currentConfig = currentConfig[keys[i]] || {};
      currentDefault = currentDefault[keys[i]] || {};
    }
    currentConfig[keys[keys.length - 1]] = currentDefault[keys[keys.length - 1]];
  });
}

/**
 * 通知其他标签页
 * @param type
 * @param payload
 */
export function notifyTab(type: string, payload?: any) {
  if (typeof BroadcastChannel !== "undefined") {
    const broadcastChannel = new BroadcastChannel("app_state_sync");
    broadcastChannel.postMessage({
      type,
      payload,
      senderId: sessionStorage.getItem("instanceId"),
    });
    broadcastChannel.close();
  }
}

/**
 * 加载 Turnstile 脚本
 */
export function loadTurnstileScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const scriptUrl = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    const script = document.createElement("script");
    script.src = scriptUrl;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Turnstile"));
    document.head.appendChild(script);
  });
}

export function formatAmount(currency: string, amount: number) {
  if (amount === 0) return "N/A";
  const name = currency ? currencyFormats[currency] : "$";
  return `${name} ${amount / 100}`;
}

/**
 * 生成浏览器指纹
 */
export function generateBrowserFingerprint(): string {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.textBaseline = "top";
    ctx.font = "14px Arial";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("Browser fingerprinting", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("Browser fingerprinting", 4, 17);
  }
  const canvasData = canvas.toDataURL();

  const plugins = Array.from(navigator.plugins || [])
    .map(plugin => plugin.name)
    .join(",");

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const screenInfo = `${screen.width}x${screen.height}x${screen.colorDepth}`;

  // 组合所有信息生成指纹
  const fingerprintData = [
    navigator.userAgent,
    screenInfo,
    timezone,
    plugins,
    canvasData,
    navigator.language,
    navigator.onLine,
  ].join("##");

  // 简单的哈希函数
  let hash = 0;
  for (let i = 0; i < fingerprintData.length; i++) {
    const char = fingerprintData.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // 转换为32位整数
  }

  return Math.abs(hash).toString(36); // 转换为36进制字符串
}

export function initSession(): void {
  const storagedId = localStorage.getItem("teb_session");
  if (!storagedId) {
    const sessionId = generateID();
    localStorage.setItem("teb_session", sessionId);
  }
}

export function getSessionId(): string {
  const storagedId = localStorage.getItem("teb_session");
  if (storagedId) return storagedId;
  const sessionId = generateID();
  localStorage.setItem("teb_session", sessionId);
  return sessionId;
}

/**
 * 下载文件
 * @param blob
 * @param filename
 */
export function downloadFile(blob: Blob, filename: string) {
  saveAs(blob, filename);
}
